import { eventBus } from '@/libs/eventBus';
import { queryClient } from '@/libs/tanstackQuery';
import { getTokens, parseIdToken } from '@repo/single-spa';
import posthog from 'posthog-js';
import { z } from 'zod';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { TokenSchema } from '../constants/schema';
import { hasTokenExpired } from '../utils/hasTokenExpired';
import { clearProfileOverride, getProfileOverride, setProfileOverride } from '../utils/profileOverride';
import { createSelectors } from './utils';

type AuthenticatedUser = z.infer<typeof TokenSchema>;

// TT-137: until the idToken catches up (next login), merge a just-completed
// profile's saved values into the user so the gate + DIP see them. The token is
// authoritative once it carries both — at that point any override is dropped.
function applyProfileOverride(user: AuthenticatedUser): AuthenticatedUser {
  if (user.birthdate && user.phone_number) {
    // The token now carries both values, so any stale override is redundant.
    // Only touch localStorage when there's actually something to clear, instead
    // of writing on every hydrate / signIn for already-complete users.
    if (getProfileOverride()) {
      clearProfileOverride();
    }
    return user;
  }
  const override = getProfileOverride();
  if (!override || override.sub !== user.sub) {
    return user;
  }
  return {
    ...user,
    birthdate: user.birthdate ?? override.birthdate,
    phone_number: user.phone_number ?? override.phone_number,
  };
}

export type AuthState = {
  user: AuthenticatedUser | undefined;
  isAuthenticated: () => boolean;
  status: 'idle' | 'success' | 'error';
  signIn: (token: string) => void;
  signOut: () => void;
  hydrate: () => void;
  // TT-137: record a just-completed profile (persisted + in-memory) so the gate
  // clears and DIP sees the values immediately and across reloads, WITHOUT a
  // re-login. The token catches up on the next natural login.
  completeProfile: (details: { birthdate: string; phone_number: string }) => void;
};

function bootstrapApp() {
  const token = getTokens();

  const parsedToken = parseIdToken(token.idToken);

  return applyProfileOverride({ ...token, ...parsedToken });
}

export const authStore = create<AuthState>()(
  devtools((set, get) => ({
    user: undefined,
    status: 'idle',
    signIn: (idToken: string) => {
      const parsedToken = parseIdToken(idToken);

      posthog.identify(parsedToken['cognito:username'], { email: parsedToken.email, full_name: parsedToken.name });

      set({ user: applyProfileOverride(parsedToken) });
    },
    signOut: () => {
      eventBus.emit('mife:logout', {
        success: true,
      });
      posthog.reset();
      clearProfileOverride();
      set({ user: undefined });
      queryClient.clear();
    },
    isAuthenticated: () => {
      const exp = get().user?.exp;

      if (!exp) {
        return false;
      }

      const expiresInMs = exp * 1000;

      return !hasTokenExpired(expiresInMs);
    },
    hydrate: () => {
      try {
        const user = bootstrapApp();
        set({ status: 'success' });
        set({ user });
      } catch (e) {
        set({ status: 'error' });
        get().signOut();
      }
    },
    completeProfile: ({ birthdate, phone_number }) => {
      const { user } = get();
      if (!user) {
        return;
      }
      // Persist (scoped to this user) so the completion survives a reload until
      // the next login mints a token that carries the values, then patch the
      // in-memory user so the gate clears immediately.
      setProfileOverride({ sub: user.sub, birthdate, phone_number });
      set({ user: { ...user, birthdate, phone_number } });
    },
  }))
);

export const useAuth = createSelectors(authStore);
export const signOut = () => authStore.getState().signOut();
export const signIn = (token: string) => authStore.getState().signIn(token);
export const hydrateAuth = () => authStore.getState().hydrate();

// Re-exported for convenience; defined as a pure, env-free helper.
export { isProfileComplete } from '../utils/isProfileComplete';
